import React from 'react';
import styles from './UtilityTable.module.css';
import { AbilityUse, DetailedRoundData, RoundData } from '../utils/types';
import { ABILITIES } from '../utils/abilities';
import { Spinner } from 'react-bootstrap';
import AbilityUsageCell from './AbilityUsageCell';

interface UtilityTableProps {
  isOpen: boolean;
  onClose: () => void;
  rounds: RoundData[];
  roundsDetailedData: Record<number, DetailedRoundData>;
  onCellClick: (roundNumber: number, playerIndex: number, timeOffset?: number) => void;
  hasSubscription?: boolean;
}

const getAbilityIcon = (agent: string, abilityIndex: number, isUltimate: boolean): { image_url: string, name: string } => {
  const agentAbilities = ABILITIES[agent.toLowerCase()];
  if (!agentAbilities) return { image_url: '', name: '' };

  if (isUltimate) {
    return agentAbilities[0];
  }
  return agentAbilities[abilityIndex];
};

const UtilityTable: React.FC<UtilityTableProps> = ({
  isOpen,
  onClose,
  rounds,
  roundsDetailedData,
  onCellClick,
  hasSubscription = false,
}) => {
  if (!isOpen) return null;

  const isLoading = !roundsDetailedData[1]?.frames[0]?.players;

  if (isLoading) {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={e => e.stopPropagation()}>
          <div className={styles.header}>
            <h3><i className="bi bi-activity me-2"></i>Utility Usage</h3>
            <button className={styles.closeButton} onClick={onClose}>×</button>
          </div>
          <div className={styles.loadingContainer}>
            <Spinner animation="border" role="status" />
            <span>Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const getAbilityUsages = (roundData: DetailedRoundData, playerIndex: number, abilityType: string) => {
    if (!roundData.abilityUsage || !roundData.abilityUsage[playerIndex]) return [];
    return roundData.abilityUsage[playerIndex].filter(use => use.type === abilityType);
  };

  const handleUsageClick = (roundNumber: number, playerIndex: number, timeOffset: number) => {
    onClose();
    onCellClick(roundNumber, playerIndex, timeOffset);
  };

  const renderPlayerSection = (playerIndex: number, player: any) => {
    const agent = player.agent.toLowerCase();
    const agentAbilities = ABILITIES[agent];
    
    // Special case for Astra
    if (agent === 'astra') {
      return (
        <React.Fragment key={`player-${playerIndex}`}>
          {/* Player header row */}
          <tr className={styles.playerHeader}>
            <td className={styles.playerHeaderCell}>
              <div className={styles.playerInfo}>
                <img 
                  src={`/agents/${agent}.png`}
                  alt={agent}
                  className={styles.agentIcon}
                />
                <span>{player.name}</span>
              </div>
            </td>
            {rounds.map(round => (
              <td key={round.round_number} className={round.enabled ? '' : styles.disabledCell}></td>
            ))}
          </tr>
          <tr className={styles.abilityRow}>
            <td className={styles.playerCell}>
              <div className={styles.abilityWrapper}>
                <span className={styles.noDataMessage}>Unable to scrape data for Astra</span>
              </div>
            </td>
            {rounds.map(round => (
              <td key={round.round_number} className={styles.disabledCount}>-</td>
            ))}
          </tr>
        </React.Fragment>
      );
    }

    if (!agentAbilities) return null;

    return (
      <React.Fragment key={`player-${playerIndex}`}>
        {/* Player header row */}
        <tr className={styles.playerHeader}>
          <td className={styles.playerHeaderCell}>
            <div className={styles.playerInfo}>
              <img 
                src={`/agents/${agent}.png`}
                alt={agent}
                className={styles.agentIcon}
              />
              <span>{player.name}</span>
            </div>
          </td>
          {rounds.map(round => (
            <td key={round.round_number} className={round.enabled ? '' : styles.disabledCell}></td>
          ))}
        </tr>

        {/* Ability rows */}
        {[...agentAbilities.slice(1), agentAbilities[0]].map((ability, index) => {
          const isUltimate = index === agentAbilities.length - 1;
          const abilityIcon = getAbilityIcon(
            agent,
            isUltimate ? 0 : index + 1,
            isUltimate
          );

          return (
            <tr key={`${playerIndex}-${ability.key}`} className={styles.abilityRow}>
              <td className={styles.playerCell}>
                <div className={styles.abilityWrapper}>
                  <img 
                    src={abilityIcon.image_url} 
                    alt={abilityIcon.name} 
                    className={styles.abilityIcon}
                  />
                  <span>{ability.name}</span>
                </div>
              </td>
              {rounds.map((round) => {
                if (!round.enabled) return <td key={round.round_number} className={styles.disabledCell}>-</td>;
                
                const roundData = roundsDetailedData[round.round_number];
                if (!roundData) return <td key={round.round_number}>-</td>;

                const abilityType = isUltimate ? 'ultimate' : `ability_${index + 1}`;
                const abilityUsages = getAbilityUsages(roundData, playerIndex, abilityType);
                
                return (
                  <AbilityUsageCell
                    key={round.round_number}
                    roundNumber={round.round_number}
                    playerIndex={playerIndex}
                    abilityUsages={abilityUsages}
                    isUltimate={isUltimate}
                    isDisabled={!round.enabled}
                    onUsageClick={handleUsageClick}
                    agent={agent}
                    abilityIcon={abilityIcon.image_url}
                    abilityName={abilityIcon.name}
                    hasSubscription={hasSubscription}
                  />
                );
              })}
            </tr>
          );
        })}
      </React.Fragment>
    );
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h3><i className="bi bi-activity me-2"></i>Utility Usage</h3>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.abilityHeader}></th>
                {rounds.map(round => (
                  <th 
                    key={round.round_number}
                    className={round.enabled ? styles.roundHeader : styles.disabledHeader}
                  >
                    {round.round_number}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roundsDetailedData[1]?.frames[0]?.players.map((player, index) => (
                renderPlayerSection(index, player)
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UtilityTable; 